import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import CheckoutForm from './CheckoutForm';
import { useAuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAlertModal } from '../AlertModalContext';
import axios from 'axios';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (value) => {
  if (typeof value !== 'number') return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// ─── Design Tokens ────────────────────────────────────────────────────────────

const C = {
  primary:     '#3b82f6',
  primaryDark: '#2563eb',
  success:     '#10b981',
  warning:     '#f59e0b',
  purple:      '#8b5cf6',
  gray50:      '#f9fafb',
  gray100:     '#f3f4f6',
  gray200:     '#e5e7eb',
  gray400:     '#9ca3af',
  gray600:     '#6b7280',
  gray700:     '#374151',
  gray900:     '#111827',
  white:       '#ffffff',
};

// ─── Mapa de cores por plano ──────────────────────────────────────────────────

const PLAN_THEME = {
  'Plano Essencial':   { color: C.success,  icon: '🌱', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
  'Plano Empresarial': { color: C.primary,  icon: '🚀', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
  'Plano Premium':     { color: C.purple,   icon: '👑', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
  default:             { color: C.primary,  icon: '⭐', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
};

const getPlanTheme = (name) => PLAN_THEME[name] || PLAN_THEME.default;

// ─── Componente: PlanCard ─────────────────────────────────────────────────────

const PlanCard = ({ plan, onSelect, isProcessing }) => {
  const [hovered, setHovered] = useState(false);
  const theme = getPlanTheme(plan.name);
  const isRecommended = plan.recommended;
  const isFree = plan.price === 0;
  const hasPromo = plan.promotional_price && plan.promotional_price < plan.price;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        backgroundColor: C.white,
        borderRadius: '20px',
        border: isRecommended
          ? `2px solid ${theme.color}`
          : `1px solid ${hovered ? theme.color + '60' : C.gray200}`,
        boxShadow: isRecommended
          ? `0 20px 60px ${theme.color}25`
          : hovered
            ? `0 12px 40px rgba(0,0,0,0.1)`
            : `0 2px 8px rgba(0,0,0,0.05)`,
        transition: 'all 0.3s ease',
        transform: isRecommended
          ? 'scale(1.04)'
          : hovered ? 'translateY(-4px)' : 'translateY(0)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Badge Popular */}
      {isRecommended && (
        <div style={{
          position: 'absolute', top: '-1px', left: '50%', transform: 'translateX(-50%)',
          background: theme.gradient,
          color: C.white, fontSize: '11px', fontWeight: '700',
          padding: '4px 16px', borderRadius: '0 0 10px 10px',
          letterSpacing: '0.05em', textTransform: 'uppercase',
          boxShadow: `0 4px 12px ${theme.color}40`,
          whiteSpace: 'nowrap',
        }}>
          ⭐ Mais Popular
        </div>
      )}

      {/* Header do card */}
      <div style={{
        padding: isRecommended ? '36px 28px 24px' : '28px 28px 24px',
        borderBottom: `1px solid ${C.gray100}`,
        background: isRecommended ? `${theme.color}08` : 'transparent',
      }}>
        {/* Ícone + Nome */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            background: theme.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px',
            boxShadow: `0 4px 12px ${theme.color}40`,
          }}>
            {theme.icon}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: C.gray900 }}>
              {plan.name}
            </h3>
            {plan.description && (
              <p style={{ margin: 0, fontSize: '12px', color: C.gray400, marginTop: '2px' }}>
                {plan.description}
              </p>
            )}
          </div>
        </div>

        {/* Preço */}
        <div>
          {isFree ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontSize: '42px', fontWeight: '800', color: C.success, lineHeight: 1 }}>
                GRÁTIS
              </span>
            </div>
          ) : hasPromo ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: '42px', fontWeight: '800', color: theme.color, lineHeight: 1 }}>
                  {formatCurrency(plan.promotional_price)}
                </span>
                <span style={{ fontSize: '14px', color: C.gray400 }}>/mês</span>
              </div>
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '13px', color: C.gray400,
                  textDecoration: 'line-through',
                }}>
                  {formatCurrency(plan.price)}/mês
                </span>
                <span style={{
                  padding: '2px 8px', borderRadius: '999px',
                  backgroundColor: '#fef3c7', color: '#92400e',
                  fontSize: '11px', fontWeight: '700',
                }}>
                  PROMOÇÃO
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.gray400 }}>
                Após 3 meses: {formatCurrency(plan.price)}/mês
              </p>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontSize: '42px', fontWeight: '800', color: theme.color, lineHeight: 1 }}>
                {formatCurrency(plan.price)}
              </span>
              <span style={{ fontSize: '14px', color: C.gray400 }}>/mês</span>
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      <div style={{ padding: '20px 28px', flex: 1 }}>
        <p style={{ margin: '0 0 12px', fontSize: '11px', fontWeight: '700', color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          O que está incluído
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {plan.features?.map((feature, idx) => (
            <div key={`${plan.id}-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{
                width: '18px', height: '18px', borderRadius: '50%',
                backgroundColor: `${theme.color}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: '1px',
              }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 3" stroke={theme.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span style={{ fontSize: '13px', color: C.gray700, lineHeight: '1.4' }}>
                {idx === 0
                  ? <strong style={{ color: theme.color }}>{feature}</strong>
                  : feature
                }
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '20px 28px 28px' }}>
        <button
          onClick={() => onSelect(plan)}
          disabled={isProcessing}
          style={{
            width: '100%',
            padding: '13px',
            background: isRecommended ? theme.gradient : C.white,
            color: isRecommended ? C.white : theme.color,
            border: isRecommended ? 'none' : `2px solid ${theme.color}`,
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '700',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.7 : 1,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontFamily: 'inherit',
            boxShadow: isRecommended ? `0 4px 14px ${theme.color}40` : 'none',
          }}
          onMouseEnter={e => {
            if (!isProcessing && !isRecommended) {
              e.currentTarget.style.background = theme.gradient;
              e.currentTarget.style.color = C.white;
              e.currentTarget.style.boxShadow = `0 4px 14px ${theme.color}40`;
            }
          }}
          onMouseLeave={e => {
            if (!isProcessing && !isRecommended) {
              e.currentTarget.style.background = C.white;
              e.currentTarget.style.color = theme.color;
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          {isProcessing && isFree ? (
            <>
              <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
              Ativando...
            </>
          ) : isFree ? (
            <>🎉 Começar Grátis</>
          ) : (
            <>🚀 Assinar Agora</>
          )}
        </button>
      </div>
    </div>
  );
};

// ─── Componente: FAQ ──────────────────────────────────────────────────────────

const faqs = [
  { q: 'Posso cancelar a qualquer momento?', a: 'Sim! Não há fidelidade. Você pode cancelar sua assinatura quando quiser, sem multas.' },
  { q: 'O que são créditos mensais?', a: 'Cada crédito equivale a um agendamento realizado no sistema. Créditos não utilizados não acumulam para o mês seguinte.' },
  { q: 'Posso mudar de plano depois?', a: 'Sim, você pode fazer upgrade ou downgrade do seu plano a qualquer momento pelo painel.' },
  { q: 'Como funciona o período promocional?', a: 'O preço promocional é válido pelos primeiros 3 meses. Após esse período, o valor retorna ao preço normal do plano.' },
];

const FAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ maxWidth: '640px', margin: '0 auto' }}>
      {faqs.map((faq, i) => (
        <div
          key={i}
          style={{
            marginBottom: '8px',
            borderRadius: '12px',
            border: `1px solid ${open === i ? C.primary + '40' : C.gray200}`,
            overflow: 'hidden',
            transition: 'border-color 0.2s',
          }}
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            style={{
              width: '100%', padding: '16px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: open === i ? `${C.primary}08` : C.white,
              border: 'none', cursor: 'pointer', textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: '600', color: C.gray900 }}>{faq.q}</span>
            <span style={{ fontSize: '18px', color: C.primary, transition: 'transform 0.2s', transform: open === i ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
          </button>
          {open === i && (
            <div style={{ padding: '0 20px 16px', fontSize: '13px', color: C.gray600, lineHeight: '1.6', backgroundColor: `${C.primary}08` }}>
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Componente Principal: PricingPage ───────────────────────────────────────

const PricingPage = () => {
  const [plans,        setPlans]        = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { authState, fetchAndUpdateSubscription } = useAuthContext();
  const navigate     = useNavigate();
  const { showAlertModal } = useAlertModal();

  // ── Lógica original preservada ────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError(null);

    axios.get('/api/plans')
      .then(response => {
        const plansWithFeatures = response.data.map((plan, index) => {
          const appointmentFeature = `Até ${plan.appointment_limit} créditos no mês`;
          let planBaseFeatures;

          if (plan.name === 'Plano Essencial') {
            planBaseFeatures = [
              'Cadastro de profissionais ilimitados',
              'Acesso ao módulo Financeiro',
              'Relatórios de Faturamento',
              'Relatórios de Pagamento',
              'Relatórios de Gerenciais',
              'Cadastro de pacientes ilimitados',
              'Acesso ao módulo de Estoque',
              'Acesso ao módulo de Pessoal',
              'Acesso ao módulo de agendamentos',
              'Gerenciamento de Perfil',
              'Suporte via e-mail',
            ];
          } else if (plan.name === 'Plano Empresarial') {
            planBaseFeatures = [
              'Cadastro de profissionais ilimitados',
              'Acesso ao módulo Financeiro',
              'Acesso ao Fluxo de Caixa',
              'Acesso ao Resultado DRE',
              'Ticket Médio e Taxa de retenção',
              'Análise de custos e despesas',
              'Relatório Faturamento por Paciente',
              'Rel. Faturamento Período e por Serviço',
              'Relatório de pagamentos recebidos',
              'Rel. Pagamentos pendentes/inadimplentes',
              'Cadastro de pacientes ilimitados',
              'Acesso ao módulo de Estoque',
              'Acesso ao módulo de Pessoal',
              'Acesso ao módulo de agendamentos',
              'Gerenciamento de Perfil',
              'Suporte via e-mail',
            ];
          } else {
            planBaseFeatures = [
              'Cadastro de profissionais ilimitados',
              'Acesso ao módulo Financeiro',
              'Acesso ao Fluxo de Caixa, DRE',
              'Ticket Médio e Taxa de retenção',
              'Análise de custos e despesas',
              'Faturamento por Paciente Período e Serviço',
              'Relatório de pagamentos recebidos',
              'Pagamentos pendentes/inadimplentes',
              'Cadastro de pacientes ilimitados',
              'Acesso ao módulo de Estoque',
              'Acesso ao módulo de Pessoal',
              'Acesso ao módulo de agendamentos',
              'Gerenciamento de Perfil',
              'Suporte via e-mail',
            ];
          }

          return {
            ...plan,
            features: [appointmentFeature, ...planBaseFeatures],
            recommended: index === 1,
          };
        });

        const isOnActiveFreePlan =
          authState.subscription &&
          authState.subscription.status === 'active' &&
          authState.subscription.plan?.price === 0;

        const finalPlans = isOnActiveFreePlan
          ? plansWithFeatures.filter(p => p.price > 0)
          : plansWithFeatures;

        setPlans(finalPlans);
      })
      .catch(() => setError('Não foi possível carregar os planos. Tente novamente mais tarde.'))
      .finally(() => setLoading(false));
  }, [authState.subscription]);

  const handlePlanSelect = async (plan) => {
    if (plan.price === 0) {
      setIsProcessing(true);
      setError(null);
      try {
        const response = await axios.post('/api/subscriptions/create-payment-link', { plan_id: plan.id });
        showAlertModal('Sucesso', response.data.message || 'Plano gratuito ativado com sucesso!', 'success');
        if (fetchAndUpdateSubscription) await fetchAndUpdateSubscription();
        setTimeout(() => navigate('/dashboard'), 1500);
      } catch (err) {
        showAlertModal('Erro', err.response?.data?.message || 'Ocorreu um erro ao ativar o plano gratuito.', 'danger');
      } finally {
        setIsProcessing(false);
      }
    } else {
      setSelectedPlan(plan);
    }
  };

  const handleSubscriptionSuccess = () => setSelectedPlan(null);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', gap: '16px', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: `3px solid #e5e7eb`, borderTopColor: C.primary, animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: C.gray400, fontSize: '14px', margin: 0 }}>Carregando planos...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Erro ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', fontFamily: "'Inter', sans-serif", padding: '24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
          <h3 style={{ color: C.gray900, marginBottom: '8px' }}>Algo deu errado</h3>
          <p style={{ color: C.gray600, marginBottom: '20px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: C.white, border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // ── Render principal ──────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: C.gray50, minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(145deg, #1e3a5f 0%, #1e40af 50%, #3b82f6 100%)',
        padding: '64px 24px 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorações */}
        <div style={{ position: 'absolute', top: '-80px', left: '-80px', width: '300px', height: '300px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', bottom: '-60px', right: '-60px', width: '250px', height: '250px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', top: '30%', right: '15%', width: '100px', height: '100px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.03)' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Pill badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 16px', borderRadius: '999px', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '20px' }}>
            <span style={{ fontSize: '14px' }}>💳</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.9)', letterSpacing: '0.04em' }}>PLANOS E PREÇOS</span>
          </div>

          <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: '800', color: '#fff', margin: '0 0 16px', lineHeight: '1.2' }}>
            Escolha o plano ideal<br />
            <span style={{ color: '#93c5fd' }}>para sua clínica</span>
          </h1>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.75)', maxWidth: '480px', margin: '0 auto', lineHeight: '1.6' }}>
            Planos flexíveis que crescem com você. Sem fidelidade, cancele quando quiser.
          </p>

          {/* Trust badges */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '32px', flexWrap: 'wrap' }}>
            {[
              { icon: '🔒', text: 'Pagamento seguro' },
              { icon: '✅', text: 'Sem fidelidade' },
              { icon: '🔄', text: 'Cancele quando quiser' },
            ].map(b => (
              <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>{b.icon}</span>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', fontWeight: '500' }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Cards de planos ── */}
      <div style={{ maxWidth: '1100px', margin: '-40px auto 0', padding: '0 24px 64px', position: 'relative', zIndex: 2 }}>

        {/* Grid de planos */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)`,
          gap: '20px',
          alignItems: 'start',
        }}
          className="pricing-grid"
        >
          {plans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onSelect={handlePlanSelect}
              isProcessing={isProcessing}
            />
          ))}
        </div>

        {/* ── Seção de comparação rápida ── */}
        <div style={{ marginTop: '64px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ height: '1px', width: '40px', backgroundColor: C.gray200 }} />
            <span style={{ fontSize: '11px', fontWeight: '700', color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Por que nos escolher</span>
            <div style={{ height: '1px', width: '40px', backgroundColor: C.gray200 }} />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: C.gray900, margin: '0 0 40px' }}>
            Tudo que sua clínica precisa
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '64px' }}>
            {[
              { icon: '📅', title: 'Agenda Inteligente',    desc: 'Gerencie consultas com facilidade' },
              { icon: '💊', title: 'Prontuário Digital',    desc: 'Histórico completo dos pacientes' },
              { icon: '💰', title: 'Gestão Financeira',     desc: 'Controle total de receitas e despesas' },
              { icon: '📊', title: 'Relatórios Avançados',  desc: 'Dados para decisões inteligentes' },
              { icon: '👥', title: 'Gestão de Equipe',      desc: 'Controle de acesso por perfil' },
              { icon: '📦', title: 'Controle de Estoque',   desc: 'Materiais sempre sob controle' },
            ].map(f => (
              <div key={f.title} style={{ padding: '20px', backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.gray200}`, textAlign: 'center', transition: 'all 0.2s' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>{f.icon}</div>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: C.gray900 }}>{f.title}</p>
                <p style={{ margin: 0, fontSize: '12px', color: C.gray400, lineHeight: '1.4' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── FAQ ── */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ height: '1px', width: '40px', backgroundColor: C.gray200 }} />
            <span style={{ fontSize: '11px', fontWeight: '700', color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Dúvidas frequentes</span>
            <div style={{ height: '1px', width: '40px', backgroundColor: C.gray200 }} />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: C.gray900, margin: '0 0 32px' }}>
            Perguntas frequentes
          </h2>
          <FAQ />
        </div>

        {/* ── CTA final ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a5f, #1e40af)',
          borderRadius: '20px',
          padding: '48px 32px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#fff', margin: '0 0 12px' }}>
              Pronto para transformar sua clínica?
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: '0 0 24px' }}>
              Junte-se a centenas de clínicas que já usam o FluxoClínica.
            </p>
            <button
              onClick={() => { const recommended = plans.find(p => p.recommended); if (recommended) handlePlanSelect(recommended); }}
              style={{
                padding: '13px 32px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#fff', border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: '0 4px 14px rgba(59,130,246,0.5)',
                transition: 'all 0.2s',
              }}
            >
              🚀 Começar agora
            </button>
          </div>
        </div>
      </div>

      {/* ── Modal de Checkout (lógica original preservada) ── */}
      <Modal
        show={!!selectedPlan && selectedPlan.price > 0}
        onHide={handleSubscriptionSuccess}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.gray100}` }}>
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px', fontWeight: '700' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              background: getPlanTheme(selectedPlan?.name).gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px',
            }}>
              {getPlanTheme(selectedPlan?.name).icon}
            </div>
            Finalizar Assinatura: {selectedPlan?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          {selectedPlan && (
            <CheckoutForm
              plan={selectedPlan}
              onSubscriptionSuccess={handleSubscriptionSuccess}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* ── CSS responsivo ── */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          .pricing-grid {
            grid-template-columns: 1fr !important;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .pricing-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PricingPage;
